import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Typography, Switch, Popconfirm, message, Spin, Tooltip, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import ParameterSchemaEditor from './ParameterSchemaEditor';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CodeTypesPage = () => {
  const [loading, setLoading] = useState(false);
  const [codeTypes, setCodeTypes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCodeType, setEditingCodeType] = useState(null);
  const [form] = Form.useForm();

  // Kod tiplerini getir
  const fetchCodeTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/codetypes', {
        params: { includeInactive: true }
      });

      if (Array.isArray(response.data)) {
        setCodeTypes(response.data);
      } else {
        console.error('Beklenmeyen API yanıtı:', response.data);
        message.error('Kod tipleri yüklenirken bir hata oluştu');
        setCodeTypes([]);
      }
    } catch (error) {
      console.error('Kod tipleri yükleme hatası:', error.response || error);
      message.error('Kod tipleri yüklenirken bir hata oluştu');
      setCodeTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // İlk yükleme
  useEffect(() => {
    fetchCodeTypes();
  }, [fetchCodeTypes]);

  // Kod tipi kaydet
  const handleSaveCodeType = async (values) => {
    try {
      setLoading(true);
      
      if (editingCodeType) {
        // Mevcut kodu güncelle
        await api.put(`/codetypes/${editingCodeType.id}`, values);
        message.success('Kod tipi başarıyla güncellendi');
      } else {
        // Yeni kod tipi oluştur
        await api.post('/codetypes', values);
        message.success('Kod tipi başarıyla eklendi');
      }
      
      // Modalı kapat, formu temizle ve kod tiplerini yeniden yükle
      setModalVisible(false);
      form.resetFields();
      setEditingCodeType(null);
      fetchCodeTypes();
    } catch (error) {
      console.error('Kod tipi kaydetme hatası:', error.response || error);
      message.error(error.response?.data?.error || 'Kod tipi kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Kod tipi sil
  const handleDeleteCodeType = async (id) => {
    try {
      setLoading(true);
      await api.delete(`/codetypes/${id}`);
      message.success('Kod tipi başarıyla silindi');
      fetchCodeTypes();
    } catch (error) {
      console.error('Kod tipi silme hatası:', error.response || error);
      
      if (error.response?.data?.error?.includes('kullanımda')) {
        Modal.error({
          title: 'Kod Tipi Kullanımda',
          content: `Bu kod tipi ${error.response.data.usedCount} yerde kullanılıyor ve silinemez. Lütfen önce bu kodu kullanan markaları güncelleyin.`
        });
      } else {
        message.error('Kod tipi silinirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  // Düzenleme modalını aç
  const handleEdit = (record) => {
    setEditingCodeType(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      description: record.description,
      parameterSchema: record.parameterSchema,
      isActive: record.isActive
    });
    setModalVisible(true);
  };

  // Parametre şemasını görüntüle
  const viewParameterSchema = (record) => {
    Modal.info({
      title: `${record.name} Parametre Şeması`,
      width: 700,
      content: (
        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
          <pre>{JSON.stringify(record.parameterSchema, null, 2)}</pre>
        </div>
      ),
      onOk() {}
    });
  };

  // Tablo sütunları
  const columns = [
    {
      title: 'Kod',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code)
    },
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: 'Parametre Sayısı',
      key: 'parameterCount',
      render: (_, record) => {
        const count = Array.isArray(record.parameterSchema) ? record.parameterSchema.length : 0;
        return (
          <Space>
            <span>{count}</span>
            {count > 0 && (
              <Button 
                type="link" 
                icon={<EyeOutlined />} 
                onClick={() => viewParameterSchema(record)}
                size="small"
              >
                Görüntüle
              </Button>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
      filters: [
        { text: 'Aktif', value: true },
        { text: 'Pasif', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: 'İşlemler',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Bu kod tipini silmek istediğinize emin misiniz?"
            description="Bu işlem geri alınamaz."
            onConfirm={() => handleDeleteCodeType(record.id)}
            okText="Evet"
            cancelText="Hayır"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Title level={3}>Kod Tipleri Yönetimi</Title>
      <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
        Marka kodları için tip tanımlamalarını burada yapabilirsiniz. Her kod tipi için farklı parametre yapıları tanımlayabilirsiniz.
      </Text>

      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCodeType(null);
            form.resetFields();
            // Varsayılan değerler
            form.setFieldsValue({
              parameterSchema: [],
              isActive: true
            });
            setModalVisible(true);
          }}
        >
          Yeni Kod Tipi Ekle
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={codeTypes}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingCodeType ? 'Kod Tipi Düzenle' : 'Yeni Kod Tipi Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingCodeType(null);
          form.resetFields();
        }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              setModalVisible(false);
              setEditingCodeType(null);
              form.resetFields();
            }}
          >
            İptal
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={() => form.submit()}
            loading={loading}
          >
            Kaydet
          </Button>
        ]}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveCodeType}
        >
          <Form.Item
            name="code"
            label="Kod"
            rules={[
              { required: true, message: 'Lütfen kod giriniz' },
              { 
                pattern: /^[a-z0-9_]+$/, 
                message: 'Kod sadece küçük harf, rakam ve alt çizgi içerebilir' 
              }
            ]}
            tooltip="Sistem içinde kullanılacak benzersiz kod (örn: google_analytics, ticimax_api)"
          >
            <Input placeholder="Örn: google_analytics" />
          </Form.Item>

          <Form.Item
            name="name"
            label="İsim"
            rules={[{ required: true, message: 'Lütfen isim giriniz' }]}
            tooltip="Kullanıcıya gösterilecek isim"
          >
            <Input placeholder="Örn: Google Analytics" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
            tooltip="Kod tipi hakkında açıklama"
          >
            <TextArea 
              placeholder="Bu kod tipi ne için kullanılır?" 
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>

          <Form.Item
            name="parameterSchema"
            label="Parametre Şeması"
            tooltip="Bu kod tipi için gerekli parametreler"
            initialValue={[]}
            rules={[
              { 
                validator: (_, value) => {
                  if (!Array.isArray(value)) {
                    return Promise.reject('Parametre şeması bir dizi olmalıdır');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <ParameterSchemaEditor />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Aktif"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CodeTypesPage; 